import { SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { getTransformDataForReset } from '@pages/WindowsCreateOrEdit/components/helpers/getTransformDataForReset'
import { ArraySectionsType, useHandlersParams } from '@pages/WindowsCreateOrEdit/types'

import {
  useCreateWindowMutation,
  useFetchWindowByIdQuery,
  useUpdateWindowMutation,
} from '@redux/api/windows.api'

import { usePrompt } from '@hooks'
import { FIELD_BIND_TYPE_ENUM, ROUTES } from '@constants'

export const useHandlers = ({
  dirtyFields,
  reset,
  watchInternalId,
  watchWindowTitle,
  watchFilterFor,
  watchFilter,
  watchSections,
  handleSubmit,
  isDirty,
}: useHandlersParams) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const location = useLocation()
  const isCreate = ROUTES.CONFIG_WINDOWS_CREATE === location.pathname
  const isEdit = Boolean(id)
  const windowId = id
  const navigate = useNavigate()
  const [currentTab, setCurrentTab] = useState<number>(0)
  const [skipDirty, setSkipDirty] = useState(false)

  const { data: windowById, isLoading: isLoadingWindowById } = useFetchWindowByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  })

  const [createWindow, { isSuccess: isSuccessCreateWindow, isLoading: isLoadingCreateWindow }] =
    useCreateWindowMutation()
  const [updateWindow, { isLoading: isLoadingUpdateWindow }] = useUpdateWindowMutation()

  const isLeaveCreate =
    isCreate &&
    !skipDirty &&
    !isSuccessCreateWindow &&
    !isLoadingCreateWindow &&
    !windowById &&
    isDirty &&
    !isLoadingWindowById

  const isLeaveEdit =
    !isCreate &&
    !!windowById &&
    !skipDirty &&
    isDirty &&
    !isLoadingWindowById &&
    !isLoadingUpdateWindow

  usePrompt({ when: isLeaveEdit || isLeaveCreate })

  const handleTabChange = (event: SyntheticEvent, tabIndex: number) => {
    if (
      Object.keys(dirtyFields).find(field => field !== 'title') &&
      !confirm(t('notifications.leave'))
    ) {
      return
    }

    reset()
    setCurrentTab(tabIndex)
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_WINDOWS)
  }

  const handleSave = handleSubmit((window: ArraySectionsType) => {
    // Общая функция для преобразования sections
    const prepareSections = () =>
      watchSections && watchSections.length > 0
        ? watchSections.map(section => ({
            ...section,
            id: section.id ?? null,
            tabs: prepareTabsData(section.tabs),
          }))
        : null

    // Функция для преобразования tabs
    const prepareTabsData = tabs =>
      tabs?.map((tab, index) => ({
        ...tab,
        id: tab.id ?? null,
        order: index + 1,
        objectCode: tab.object?.id ?? null,
        objectTitle: tab.object?.label ?? null,
        entityId: tab.entity?.id ?? null,
        entityCode: tab.entity?.code ?? null,
        entityType: tab.entityType?.label ?? null,
        conditions: tab.conditions ?? '',
        actions: prepareActionsData(tab.actions),
      })) ?? null

    // Функция для преобразования actions
    const prepareActionsData = actions =>
      actions?.map(action => ({
        ...action,
        id: action?.id ?? null,
        actionCode: action.actionTitle?.id ?? '',
        actionTitle: action.actionTitle?.label ?? '',
        events: prepareEventsData(action.events),
      })) ?? null

    // Функция для преобразования events
    const prepareEventsData = events =>
      events?.map(event => ({
        ...event,
        id: event.id ?? null,
        eventCode: (() => {
          if (id && event?.event?.id && typeof event.event.id === 'string') {
            return event?.event?.id
          } else {
            return event.event?.label ?? ''
          }
        })(),
        eventTitle: event.event?.label ?? '',
        varId: event.postVariable?.id ?? null,
        varTitle: event.postVariable?.label ?? '',
        valueType: event?.valueType?.label ? (event.valueType.label as string).toUpperCase() : null,
        value:
          event.valueType?.label === FIELD_BIND_TYPE_ENUM.FIELD && event.objectValue
            ? event.objectValue
            : event.value || null,
      })) ?? null

    // Общие данные для обоих запросов
    const commonData = {
      title: window.title || watchWindowTitle,
      internalId: window.internalId || watchInternalId,
      filterId: watchFilter?.id as number,
      sections: prepareSections(),
      vars: window?.vars || null,
      configs: window?.configs || null,
    }

    // Данные для создания
    const createData = {
      ...commonData,
      id: null,
      filterSectionId: null,
      filterSectionUuid:
        window?.sections && window?.sections[watchFilterFor?.id as number]?.uuid
          ? window.sections[watchFilterFor?.id as number].uuid
          : watchSections[watchFilterFor?.id as number]?.uuid,
    }

    // Данные для обновления
    const updateData = {
      ...commonData,
      id,
      filterSectionId: window.sections?.[watchFilterFor?.id as number]?.uuid
        ? null
        : window?.filterSection?.id || null,
      filterSectionTitle:
        window.filterSection && window.filterSection?.id ? window.filterSection.label : null,
      filterSectionUuid: watchFilterFor?.id
        ? watchSections[watchFilterFor?.id as number]?.uuid
        : window.filterSectionUuid ||
          window?.sections?.[watchFilterFor?.id as number]?.uuid ||
          null,
    }

    // Общая функция для обработки результата
    const handleResponse = res => {
      if (!res) {
        handleCancel()

        return
      }

      if (!id && res) {
        navigate(`${ROUTES.CONFIG_WINDOWS_EDIT}/${res?.id}`)
      } else if (id) {
        reset(getTransformDataForReset(res))
      }
    }

    // Выполнение запроса в зависимости от наличия id
    const apiCall = !id ? createWindow(createData) : updateWindow(updateData)

    apiCall
      .unwrap()
      .then(handleResponse)
      .finally(() => setSkipDirty(false))
  })

  return {
    state: {
      currentTab,
    },
    data: {
      t,
      id,
      isCreate,
      windowById,
      isLoadingWindowById,
      isLoadingWindow: isLoadingUpdateWindow || isLoadingCreateWindow,
      windowId,
      isEdit,
    },
    handlers: { handleTabChange, handleCancel, handleSave },
  }
}
